import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomButton, HeadingText } from "../../utils/CustomTagComponents";
import orangeIcon from "../../assets/icons/tabler_book.png";
import LabeledInput from "../LabeledInput ";
import CustomSelect from "../CustomSelect ";
import axios from "axios";
import { useSelector } from "react-redux";
import { showToast } from "../common/Toast";
import { submitContactForm } from "../../services/ContactFormService";
import { motion } from "framer-motion";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PublicIcon from "@mui/icons-material/Public";
// import contactImage from "../../assets/images/contact_img.png";
import study from "../../assets/images/study.jpg";
import work from "../../assets/images/work.jpeg";
import healthcare from "../../assets/images/healthcare.webp";
import culture from "../../assets/images/culture.jpg";
import visit from "../../assets/images/visit.jpg";

const allMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WhyStudyGermany = ({ title = "", data = [] }) => {
  const { country_id, country_name, auth } = useSelector((state) => state.auth);
  const userId = auth?.localId;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    service: "",
    month: "",
    year: "",
    consent: false,
  });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const leftVariants = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  const rightVariants = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  const [errors, setErrors] = useState({});
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getResponsiveTitle = (html) => {
    if (!html) return "";

    let clean = html
      // .replace(/style="[^"]*"/gi, "") // ✅ REMOVE ALL inline styles
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/&nbsp;/gi, "")
      .replace(/<br\s*\/?>/gi, "");

    if (!isMobile) return clean;

    return clean
      .replace(/font-weight:\s*900/gi, "font-weight: 700");
  };

  // Generate years (current + next 10)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const yearArr = Array.from({ length: 11 }, (_, i) =>
      String(currentYear + i)
    );
    setYears(yearArr);
  }, []);

  // Dynamically handle month/year list
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIndex = now.getMonth();

    if (!formData.year) {
      setFormData((prev) => ({
        ...prev,
        year: String(currentYear),
        month: allMonths[currentMonthIndex],
      }));
      setMonths(allMonths.slice(currentMonthIndex));
    } else if (parseInt(formData.year) === currentYear) {
      setMonths(allMonths.slice(currentMonthIndex));
      if (
        !formData.month ||
        allMonths.indexOf(formData.month) < currentMonthIndex
      ) {
        setFormData((prev) => ({
          ...prev,
          month: allMonths[currentMonthIndex],
        }));
      }
    } else {
      setMonths(allMonths);
      if (!formData.month)
        setFormData((prev) => ({ ...prev, month: "January" }));
    }
  }, [formData.year]);

  // Handle change
  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validation
  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    else if (!/^[0-9]{10}$/.test(formData.mobile))
      newErrors.mobile = "Enter a valid 10-digit number";
    if (!formData.service.trim())
      newErrors.service = "Preferred service is required";
    if (!formData.month) newErrors.month = "Select a month";
    if (!formData.year) newErrors.year = "Select a year";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      firstname: formData.name,
      lastname: "",
      email: formData.email,
      mobile: formData.mobile,
      preferred_destination: formData.service,
      preferred_plan: `${formData.month}`, // if plan type needed, replace with another field
      preferred_year: formData.year,
      consent_enable: formData.consent,
      user_id: userId, // static for now — replace with logged-in user
      country_id: country_id, // static for now — replace dynamically if needed
    };

    try {
      const res = await submitContactForm(payload);
      if (res.data.success) {
        showToast("Form submitted Successfully");
        setFormData({
          name: "",
          email: "",
          mobile: "",
          service: "",
          month: allMonths[new Date().getMonth()],
          year: String(new Date().getFullYear()),
          consent: false,
        });
      } else {
        showToast("Error while submitting form", "error");
      }
    } catch (err) {
      console.error("Error:", err);
      showToast("Error while submitting form", "error");
    }
  };

  const titleStyle = {
    fontSize: { xs: 14, sm: 18 },
    fontWeight: 700,
    color: "#000",
    lineHeight: { xs: "14px", md: "18px" },
    mb: 0
  };

  const descStyle = {
    fontSize: { xs: 13, sm: 16 },
    fontWeight: 400,
    color: "#000",
    lineHeight: { xs: "20px", md: 1.6 },
    textAlign: "justify",
  };

  const cardStyle = {
    borderRadius: "16px",
    p: 3,
    background: "#ffffff", // ✅ FIXED (solid color)
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    height: "100%",
    transition: "all 0.3s ease",
    position: "relative",

    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 16px 40px rgba(0,0,0,0.1)",
    }
  };
  const getIcon = (title) => {
    const key = title?.toLowerCase();

    if (key?.includes("visit")) return <TravelExploreIcon />;
    if (key?.includes("culture")) return <PublicIcon />;
    if (key?.includes("study")) return <SchoolIcon />;
    if (key?.includes("work")) return <WorkIcon />;
    if (key?.includes("health")) return <HealthAndSafetyIcon />;

    return <InfoOutlinedIcon />;
  };

  return (
    <Container
      sx={{
        py: { xs: 2, sm: 2, md: 3 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}
    >
      {/* Heading */}
      <Box
        sx={{
          display: "inline-block",
          position: "relative",
          mb: 2,

          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            bottom: -5,
            width: "200px",
            height: "2px",
            backgroundColor: "#fd0000",
            opacity: "0.5"
          },
        }}
      >
        <HeadingText
          sx={{ fontSize: "30px !important" }}
          dangerouslySetInnerHTML={{ __html: getResponsiveTitle(title) }}
        />
      </Box>
      {/* -------- ROW 1 -------- */}
      {data?.[0]?.value &&
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            alignItems: "center",
            mt: 2
          }}
        >
          {/* LEFT - Visit */}
          <Box
            component={motion.div}
            variants={leftVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={cardStyle} className="about-card">
            {/* Title + Icon */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "#fd0000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}>
                {getIcon(data?.[0]?.key)}
              </Box>

              <Typography sx={titleStyle}>
                {data?.[0]?.key}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                "& p": {
                  display: "block",
                  margin: "0 0 16px 0 !important",
                },

                "& p:last-child": {
                  marginBottom: "0 !important",
                },
                ...descStyle
              }}
              dangerouslySetInnerHTML={{
                __html: data?.[0]?.value,
              }}
            />
          </Box>

          {/* RIGHT - Image */}
          <Box
            component={motion.div}
            variants={rightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={visit}
              alt="Visit"
              sx={{
                width: "100%",
                borderRadius: "20px",
                objectFit: "cover",
                height: { xs: 250, md: 380 },
              }}
            />
          </Box>
        </Box>
      }

      {/* -------- ROW 2 -------- */}
      {data?.[1]?.value &&
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mt: 3
          }}
        >

          {/* LEFT - Image */}
          <Box
            component={motion.div}
            variants={leftVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={culture}
              alt="Culture"
              sx={{
                width: "100%",
                borderRadius: "20px",
                objectFit: "cover",
                // maxWidth: { xs: 300, sm: 400, md: 500 },
                height: { xs: 250, md: 370 },
              }}
            />
          </Box>

          {/* Culture */}
          <Box
            component={motion.div}
            variants={rightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={cardStyle}
            className="about-card"
          >
            {/* Title + Icon */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "#fd0000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}>
                {getIcon(data?.[1]?.key)}
              </Box>

              <Typography sx={titleStyle}>
                {data?.[1]?.key}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                "& p": {
                  display: "block",
                  margin: "0 0 16px 0 !important",
                },

                "& p:last-child": {
                  marginBottom: "0 !important",
                },
                ...descStyle
              }}
              dangerouslySetInnerHTML={{
                __html: data?.[1]?.value,
              }}
            />
          </Box>

        </Box>
      }

      {/* -------- ROW 3 -------- */}

      {data?.[2]?.value &&
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mt: 3
          }}
        >
          {/* Study */}
          <Box
            component={motion.div}
            variants={leftVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={cardStyle}
            className="about-card"
          >
            {/* Title + Icon */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "#fd0000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}>
                {getIcon(data?.[2]?.key)}
              </Box>

              <Typography sx={titleStyle}>
                {data?.[2]?.key}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                "& p": {
                  display: "block",
                  margin: "0 0 16px 0 !important",
                },

                "& p:last-child": {
                  marginBottom: "0 !important",
                },
                ...descStyle
              }}
              dangerouslySetInnerHTML={{
                __html: data?.[2]?.value,
              }}
            />
          </Box>

          {/* RIGHT - Image */}
          <Box
            component={motion.div}
            variants={rightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={study}
              alt="Study"
              sx={{
                width: "100%",
                borderRadius: "20px",
                objectFit: "cover",
                // maxWidth: { xs: 300, sm: 400, md: 500 },
                height: { xs: 250, md: 350 },
              }}
            />
          </Box>

        </Box>
      }

      {/* -------- ROW 4 -------- */}

      {data?.[3]?.value &&
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mt: 3
          }}
        >

          {/* RIGHT - Image */}
          <Box
            component={motion.div}
            variants={leftVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={work}
              alt="Work"
              sx={{
                width: "100%",
                borderRadius: "20px",
                objectFit: "cover",
                // maxWidth: { xs: 300, sm: 400, md: 500 },
                height: { xs: 250, md: 420 },
              }}
            />
          </Box>

          {/* Work */}
          <Box
            component={motion.div}
            variants={rightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={cardStyle}
            className="about-card"
          >
            {/* Title + Icon */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "#fd0000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}>
                {getIcon(data?.[3]?.key)}
              </Box>

              <Typography sx={titleStyle}>
                {data?.[3]?.key}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                "& p": {
                  display: "block",
                  margin: "0 0 16px 0 !important",
                },

                "& p:last-child": {
                  marginBottom: "0 !important",
                },
                ...descStyle
              }}
              dangerouslySetInnerHTML={{
                __html: data?.[3]?.value,
              }}
            />
          </Box>

        </Box>
      }

      {/* -------- ROW 5 -------- */}

      {data?.[4]?.value &&
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mt: 3
          }}
        >

          {/* Healthcare */}
          <Box
            component={motion.div}
            variants={leftVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={cardStyle}
            className="about-card"
          >
            {/* Title + Icon */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "#fd0000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}>
                {getIcon(data?.[4]?.key)}
              </Box>

              <Typography sx={titleStyle}>
                {data?.[4]?.key}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                "& p": {
                  display: "block",
                  margin: "0 0 16px 0 !important",
                },

                "& p:last-child": {
                  marginBottom: "0 !important",
                },
                ...descStyle
              }}
              dangerouslySetInnerHTML={{
                __html: data?.[4]?.value,
              }}
            />
          </Box>

          {/* RIGHT - Image */}
          <Box
            component={motion.div}
            variants={rightVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={healthcare}
              alt="Healthcare"
              sx={{
                width: "100%",
                borderRadius: "20px",
                objectFit: "cover",
                // maxWidth: { xs: 300, sm: 400, md: 500 },
                height: { xs: 250, md: 300 },
              }}
            />
          </Box>

        </Box>
      }

    </Container>
  );
};

export default WhyStudyGermany;
